import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import { warningNotification } from '@shared/store/notification.duck';
import {
  getHolidayCarryOverPreviewForUser,
  performHolidayRollOverOnUser
} from '@features/holidays/services/HolidayCarryoverService';
import { getAllUsersRequest } from '@features/settings/services/UserService';
import { BasicUserInfo } from '@shared/models/user.models';

export interface CarryOverRow {
  year: number;
  userId: string;
  name: String;
  unusedAllowanceFromPreviousYear: number;
  daysToCarryOverFromPreviousYear: number;
  thisYearsAllowance: number;
  thisYearsAdditionalDays: number;
  thisYearsCarryOver: number;
  totalDays: number;
  loaded?: boolean;
  updated?: boolean;
}

export interface HolidayCarryOverState {
  loading: boolean;
  hasBeenRun: boolean;
  carryOverData: CarryOverRow[];
  totalRecords: number;
  count: number;
  skip: number;
}

export const initialHolidayCarryOverState: HolidayCarryOverState = {
  loading: false,
  hasBeenRun: false,
  carryOverData: [],
  totalRecords: 0,
  count: 10,
  skip: 0,
}

async function loadCarryOverForRowsAsync(rows: CarryOverRow[], dispatch: any) {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    try {
      const previewForUser = await getHolidayCarryOverPreviewForUser(row.userId, row.year);
      const updatedRow = {
        ...row,
        ...previewForUser,
        loaded: true,
      }
      dispatch(updateCarryOverRow({updatedRow}));
    } catch (e) {
      console.log('Failed', e);
    }
  }
}

async function runCarryOverAsync(year: number, rows: CarryOverRow[], dispatch: any) {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    try {
      await performHolidayRollOverOnUser(row.year, row.userId);
      const updatedRow = {
        ...row,
        updated: true,
        thisYearsCarryOver: row.daysToCarryOverFromPreviousYear,
      }
      dispatch(updateCarryOverRow({updatedRow}));
    } catch (e) {
      console.log('Failed', e);
    }
  }
}

export const loadCarryOverInfo: any = createAsyncThunk(
  'holidayCarryOver/load',
  async (params: {calendarYear: any, skip: number}, thunkAPI) => {
    const allUsers = await getAllUsersRequest();
    const rows = allUsers.map((user: BasicUserInfo) => ({
      year: params.calendarYear,
      name: user.firstName + ' ' + user.lastName,
      userId: user.userId,
      unusedAllowanceFromPreviousYear: 0,
      daysToCarryOverFromPreviousYear: 0,
      thisYearsAllowance: 0,
      thisYearsAdditionalDays: 0,
      thisYearsCarryOver: 0,
      totalDays: 0,
      loaded: false,
      updated: false,
    }));
    loadCarryOverForRowsAsync(rows, thunkAPI.dispatch);
    return {
      data: rows,
    }
  }
)

export const runCarryOver: any = createAsyncThunk(
  'holidayCarryOver/runCarryOver',
  async (params: {calendarYear: any}, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState);
    const data = state.holidayCarryOver.carryOverData;
    if (data.filter(d => !d.loaded).length > 0) {
      thunkAPI.dispatch(warningNotification('Wait for preview to finish loading'));
      return;
    }

    runCarryOverAsync(params.calendarYear, data, thunkAPI.dispatch);
  }
)

const updateCarryOverRow: any = createAsyncThunk(
  'holidayCarryOver/updateCarryOverRow',
  async (params: {updatedRow: CarryOverRow}, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState);
    const data = state.holidayCarryOver.carryOverData;
    const idx = data.findIndex(r => r.userId === params.updatedRow.userId && r.year === params.updatedRow.year);
    let itemToUpdate = {...data[idx], ...params.updatedRow, hasLoaded: true};
    let updatedArray = [...data];
    updatedArray.splice(idx, 1, itemToUpdate);
    return updatedArray;
  }
)

const holidayCarryOverSlice = createSlice({
  name: 'holidayCarryOver',
  initialState: initialHolidayCarryOverState,
  reducers: {
    reset: () => initialHolidayCarryOverState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCarryOverInfo.pending, (state) => ({...state, loading: true, hasBeenRun: false, }))
      .addCase(loadCarryOverInfo.rejected, (state) => ({...state, loading: false, }))
      .addCase(loadCarryOverInfo.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      carryOverData: action.payload.data,
      hasBeenRun: action.payload.hasBeenRun,
      totalRecords: action.payload.totalRecords,
      skip: action.payload.skip,
      count: action.payload.count,
    }))
      .addCase(runCarryOver.pending, (state) => ({...state, loading: true, }))
      .addCase(runCarryOver.rejected, (state) => ({...state, loading: false, }))
      .addCase(runCarryOver.fulfilled, (state, action) => ({...state, loading: false, }))
      .addCase(updateCarryOverRow.fulfilled, (state, action) => ({...state, loading: false, carryOverData: action.payload }));
  }
})

export default holidayCarryOverSlice.reducer;


// Selectors
const selectPaginationInfo = (state: AppState) => ({
  totalRecords: state.holidayCarryOver.totalRecords,
  count: state.holidayCarryOver.count,
  skip: state.holidayCarryOver.skip,
});

export const selectLoading = (state: AppState) => state.holidayCarryOver.loading;
export const selectHasBeenRun = (state: AppState) => state.holidayCarryOver.hasBeenRun;
export const selectData = (state: AppState) => state.holidayCarryOver?.carryOverData ?? [];
